import {
  Button,
  ErrorMessage,
  Input,
  notify,
  Skeleton,
  Switch,
  useConfirmModal,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { UserApiKeysModeService } from '@affine/core/modules/cloud/services/user-api-keys-mode';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../shared/settings-offline-status';
import * as styles from './ai-provider-panel.css';

/**
 * OAuth Code Input component for Claude connection.
 */
const OAuthCodeInput = ({
  onCodeChange,
}: {
  onCodeChange: (code: string) => void;
}) => {
  const [code, setCode] = useState('');

  const handleChange = (value: string) => {
    setCode(value);
    onCodeChange(value);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setCode(text.trim());
        onCodeChange(text.trim());
        notify.success({ title: 'Code pasted' });
      }
    } catch {
      notify.error({ title: 'Failed to paste from clipboard' });
    }
  };

  return (
    <div className={styles.oauthInputContainer}>
      <div className={styles.oauthInputRow}>
        <Input
          type="text"
          placeholder="Paste authorization code here..."
          value={code}
          onChange={handleChange}
          style={{ flex: 1 }}
        />
        <Button variant="primary" onClick={() => void handlePaste()}>
          Paste
        </Button>
      </div>
      <p className={styles.oauthHint}>
        The code will appear after you authorize {BRAND_DISPLAY_NAME}.
      </p>
    </div>
  );
};

/**
 * Key Input Row Component
 * Shows masked key when set, input field when editing
 */
const KeyInputRow = ({
  label,
  desc,
  isSet,
  maskedValue,
  placeholder,
  onSave,
  disabled,
}: {
  label: string;
  desc: string;
  isSet: boolean;
  maskedValue?: string | null;
  placeholder: string;
  onSave: (value: string) => Promise<void>;
  disabled: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // When key is set and not editing, show masked value or fallback to 'Configured'
  const displayValue =
    isSet && !isEditing ? maskedValue || 'Configured' : inputValue;
  const buttonLabel = isSet && !isEditing ? 'Change' : 'Save';
  const canSave = isEditing ? true : inputValue.trim().length > 0;

  const handleButtonClick = async () => {
    if (isSet && !isEditing) {
      // Start editing
      setIsEditing(true);
      setInputValue('');
      return;
    }

    // Save (empty value = delete)
    setIsSaving(true);
    try {
      await onSave(inputValue.trim());
      setInputValue('');
      setIsEditing(false);
    } catch (err) {
      notify.error({ title: 'Failed to save', message: String(err) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingRow name={label} desc={desc} spreadCol={false}>
      <div className={styles.keyInputRow}>
        <Input
          type={isSet && !isEditing ? 'text' : 'password'}
          placeholder={placeholder}
          value={displayValue}
          onChange={setInputValue}
          disabled={disabled || isSaving || (isSet && !isEditing)}
          style={{ flex: 1 }}
        />
        <Button
          variant={isSet && !isEditing ? 'secondary' : 'primary'}
          onClick={() => void handleButtonClick()}
          disabled={disabled || isSaving || (!canSave && !isSet)}
        >
          {isSaving ? 'Saving...' : buttonLabel}
        </Button>
      </div>
    </SettingRow>
  );
};

/**
 * AI Provider Settings Panel
 *
 * Simple toggle between Server Mode and User Mode:
 * - Server Mode (toggle off): Uses StreamLain's managed AI service
 * - User Mode (toggle on): User provides their own API keys
 */
export const AiProviderPanel = () => {
  const { openConfirmModal } = useConfirmModal();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const userApiKeysModeService = useService(UserApiKeysModeService);
  const userApiKeysMode = userApiKeysModeService.userApiKeysMode;

  // State from service
  const mode = useLiveData(userApiKeysMode.mode$);
  const openaiKeySet = useLiveData(userApiKeysMode.openaiKeySet$);
  const openaiKeyMasked = useLiveData(userApiKeysMode.openaiKeyMasked$);
  const webSearchKeySet = useLiveData(userApiKeysMode.webSearchKeySet$);
  const webSearchKeyMasked = useLiveData(userApiKeysMode.webSearchKeyMasked$);
  const claudeTokenSet = useLiveData(userApiKeysMode.claudeTokenSet$);
  const isRevalidating = useLiveData(userApiKeysMode.isRevalidating$);
  const isSaving = useLiveData(userApiKeysMode.isSaving$);
  const error = useLiveData(userApiKeysMode.error$);

  // OAuth state
  const [isConnecting, setIsConnecting] = useState(false);
  const authCodeRef = useRef('');

  // Revalidate on mount
  useEffect(() => {
    if (backendAvailable) {
      userApiKeysMode.revalidate();
    }
  }, [backendAvailable, userApiKeysMode]);

  const isUserMode = mode === 'user-keys';

  // Handle mode toggle
  const handleModeToggle = useCallback(
    async (checked: boolean) => {
      try {
        await userApiKeysMode.setMode(checked ? 'user-keys' : 'standard');
        notify.success({
          title: checked ? 'Switched to User Mode' : 'Switched to Server Mode',
        });
      } catch (err) {
        notify.error({ title: 'Failed to switch mode', message: String(err) });
      }
    },
    [userApiKeysMode]
  );

  // Handle OpenAI key save/delete
  const handleSaveOpenAiKey = useCallback(
    async (value: string) => {
      if (value) {
        await userApiKeysMode.setOpenAiKey(value);
        notify.success({ title: 'OpenAI API key saved' });
        // Revalidate to get masked key
        userApiKeysMode.revalidate();
      } else {
        await userApiKeysMode.deleteOpenAiKey();
        notify.success({ title: 'OpenAI API key removed' });
      }
    },
    [userApiKeysMode]
  );

  // Handle connect Claude token - OAuth flow
  const handleConnectClaude = useCallback(async () => {
    setIsConnecting(true);
    authCodeRef.current = '';

    try {
      const { authUrl, state } = await userApiKeysMode.initOAuth();
      window.open(authUrl, '_blank');

      openConfirmModal({
        title: 'Connect Claude Account',
        description:
          'A new tab has opened for Claude login. After logging in, copy the authorization code and paste it below:',
        children: (
          <OAuthCodeInput
            onCodeChange={code => {
              authCodeRef.current = code;
            }}
          />
        ),
        confirmText: 'Connect',
        cancelText: 'Cancel',
        onConfirm: async () => {
          const code = authCodeRef.current.trim();
          if (!code) {
            notify.error({ title: 'Please enter the authorization code' });
            return;
          }
          try {
            await userApiKeysMode.exchangeOAuthCode(code, state);
            notify.success({ title: 'Claude account connected' });
            authCodeRef.current = '';
          } catch (err) {
            notify.error({ title: 'Failed to connect', message: String(err) });
          }
        },
        onCancel: () => {
          authCodeRef.current = '';
        },
      });
    } catch (err) {
      notify.error({ title: 'Failed to start OAuth', message: String(err) });
    } finally {
      setIsConnecting(false);
    }
  }, [userApiKeysMode, openConfirmModal]);

  // Handle Web Search key save/delete
  const handleSaveSearchKey = useCallback(
    async (value: string) => {
      if (value) {
        await userApiKeysMode.setWebSearchKey(value);
        notify.success({ title: 'Web Search key saved' });
        // Revalidate to get masked key
        userApiKeysMode.revalidate();
      } else {
        await userApiKeysMode.deleteWebSearchKey();
        notify.success({ title: 'Web Search key removed' });
      }
    },
    [userApiKeysMode]
  );

  // Loading state
  if (isRevalidating && mode === null) {
    return (
      <SettingWrapper title="AI Provider">
        {backendAvailable ? (
          <Skeleton height={42} />
        ) : (
          <SettingRow name={offlineLabel} desc="" />
        )}
      </SettingWrapper>
    );
  }

  // Error state
  if (error && mode === null) {
    return (
      <SettingWrapper title="AI Provider">
        <ErrorMessage>Failed to load settings</ErrorMessage>
      </SettingWrapper>
    );
  }

  return (
    <SettingWrapper title="AI Provider">
      <SettingRow
        name="Use your own API keys"
        desc={
          isUserMode
            ? 'You are using your own API keys for AI features'
            : `You are using ${BRAND_DISPLAY_NAME}'s managed AI service`
        }
      >
        <Switch
          checked={isUserMode}
          onChange={checked => void handleModeToggle(checked)}
          disabled={isSaving || !backendAvailable}
        />
      </SettingRow>

      {isUserMode && (
        <>
          <KeyInputRow
            label="OpenAI API Key"
            desc="Required for Embedding, Audio Transcription, and Rerank"
            isSet={openaiKeySet ?? false}
            maskedValue={openaiKeyMasked}
            placeholder="sk-..."
            onSave={handleSaveOpenAiKey}
            disabled={isSaving || !backendAvailable}
          />

          <SettingRow
            name="Claude Token"
            desc="Optional. Improves quality for Chat, Coding, and complex tasks"
            spreadCol={false}
          >
            <div className={styles.keyInputRow}>
              <Input
                type="text"
                value={claudeTokenSet ? 'Configured' : ''}
                placeholder="Not connected"
                disabled
                style={{ flex: 1 }}
              />
              <Button
                variant="primary"
                onClick={() => void handleConnectClaude()}
                disabled={isConnecting || !backendAvailable}
              >
                {isConnecting
                  ? 'Connecting...'
                  : claudeTokenSet
                    ? 'Reconnect'
                    : 'Connect'}
              </Button>
            </div>
          </SettingRow>

          <KeyInputRow
            label="Web Search Key"
            desc="Optional. Supports Exa or Tavily API keys"
            isSet={webSearchKeySet ?? false}
            maskedValue={webSearchKeyMasked}
            placeholder="Exa or Tavily API key..."
            onSave={handleSaveSearchKey}
            disabled={isSaving || !backendAvailable}
          />
        </>
      )}
    </SettingWrapper>
  );
};
